import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, MenuItem, Select, FormControl,
  InputLabel, Skeleton, Tooltip, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useAuth } from '../../auth/AuthContext';
import { listScopedKeys, revokeScopedKey } from '../../api/partnerApi';

// ---------------------------------------------------------------------------
// ScopedKeysList — read-side of the Scoped Keys tab.
//
// The `/v1/admin/keys/scoped` GET endpoint is partner-wide: it returns ALL
// active scoped keys across both live + test tenants in one response. We
// fetch once with a test-tenant-minted token (the response shape is the
// same regardless of which tenant minted the token) and filter client-side
// by the env selector. That way switching env doesn't pay a network round
// trip and the +New button can mint its key against the selected env
// without re-fetching the list.
//
// Step 4 will wire the +New button to <ScopedKeyCreateDialog>. Until then
// onCreateClick is a placeholder.
// ---------------------------------------------------------------------------

function UserTypeChip({ userType }) {
  if (userType === 'SERVICE') {
    return (
      <Chip
        size="small"
        icon={<SmartToyIcon fontSize="small" />}
        label="SERVICE"
        sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', fontWeight: 600, fontSize: 11 }}
      />
    );
  }
  // HUMAN is the default for legacy rows (default-on-read).
  return (
    <Chip
      size="small"
      icon={<PersonIcon fontSize="small" />}
      label="HUMAN"
      sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: 11 }}
    />
  );
}

export default function ScopedKeysList({ onCreateClick }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [env, setEnv] = useState('live');
  // revokeTarget holds the row pending confirmation, or null if no dialog open.
  const [revokeTarget, setRevokeTarget] = useState(null);

  const envTenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;

  const { data: allKeys, isLoading, error } = useQuery(
    ['scopedKeys'],
    // Partner-wide query — env passed to mint a token, but the response
    // doesn't depend on it. Using 'test' keeps the live token cache cold
    // until the user actually mints a live-tenant scoped key.
    () => listScopedKeys('test'),
    { enabled: !!user }
  );

  const revokeMutation = useMutation(
    ({ keyId }) => revokeScopedKey('test', keyId),
    {
      onSuccess: () => {
        qc.invalidateQueries(['scopedKeys']);
        setRevokeTarget(null);
      },
    },
  );

  // Filter to the env the user is viewing.
  const keys = (allKeys || []).filter((k) => k.tenantId === envTenantId);

  return (
    <Box>
      {/* Header row: env selector + New button */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="body2" color="text.secondary">
          Short-lived, scope-limited keys (<code>ssk_live_*</code> / <code>ssk_test_*</code>)
          bound to a PartnerUser + AccessProfile. Use for agents, CI tooling, and
          per-team-member bots.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Environment</InputLabel>
            <Select value={env} label="Environment" onChange={(e) => setEnv(e.target.value)}>
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            disabled={!onCreateClick}
            sx={{
              bgcolor: '#3B82F6',
              '&:hover': { bgcolor: '#2563EB' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            New scoped key
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.response?.data?.message || error.message || 'Failed to load scoped keys'}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Context</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Created</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
              : keys.map((key) => (
                <TableRow key={key.keyId} hover>
                  <TableCell sx={{ fontSize: 13 }}>{key.keyName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>
                        {key.userId}
                      </Typography>
                      <UserTypeChip userType={key.userType} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{key.contextId}</TableCell>
                  <TableCell>
                    <Chip
                      label={key.status}
                      size="small"
                      sx={{
                        bgcolor: key.status === 'active' ? '#dcfce7' : '#fee2e2',
                        color: key.status === 'active' ? '#166534' : '#991b1b',
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#64748b' }}>
                    {key.createdAt ? new Date(key.createdAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Revoke scoped key"
                        onClick={() => setRevokeTarget(key)}
                        disabled={key.status !== 'active'}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && keys.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                  <Box sx={{
                    p: 6,
                    textAlign: 'center',
                    color: 'text.secondary',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                    <Typography variant="body1" color="text.primary">
                      No scoped keys for the <strong>{env}</strong> environment yet
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={onCreateClick}
                      disabled={!onCreateClick}
                      sx={{ textTransform: 'none' }}
                    >
                      New scoped key
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Revoke confirmation dialog */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke scoped key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box component="span" sx={{ display: 'block', mb: 1 }}>
              You're about to revoke <strong>{revokeTarget?.keyName}</strong>
              {' '}(bound to user {revokeTarget?.userId}).
            </Box>
            <Box component="span" sx={{ display: 'block', color: 'warning.main' }}>
              This takes effect within ~5 minutes (Rust authorizer cache window).
              Anything currently using this key will start receiving 401 errors
              once the cache flushes.
            </Box>
          </DialogContentText>
          {revokeMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeMutation.error?.response?.data?.message
                || revokeMutation.error?.message
                || 'Revoke failed'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRevokeTarget(null)}
            sx={{ textTransform: 'none' }}
            disabled={revokeMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => revokeMutation.mutate({ keyId: revokeTarget.keyId })}
            variant="contained"
            color="error"
            disabled={revokeMutation.isLoading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {revokeMutation.isLoading ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
